import React, { useEffect, useRef, useState } from 'react';
import Alert from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import { buttonClass } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import { LATEST_PROMPT } from './usePlanChatStream';
import type { UsePlanChatStream } from './usePlanChatStream';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

export interface PlanChatProps {
  chat: UsePlanChatStream;
  /** The plan's version as this page last loaded it. */
  planVersion: number;
}

const SUGGESTIONS = ['What is still undecided?', 'Summarize the most voted notes.', 'Which topics have no authority yet?'];

/** Plain text with paragraph breaks: the reply is never rendered as HTML. */
const Paragraphs: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/\n{2,}/).map((p, i) => (
      <p key={i} className="whitespace-pre-wrap break-words [&:not(:first-child)]:mt-2">
        {p}
      </p>
    ))}
  </>
);

/** Ask questions about the plan as it stands. The server reads the whole plan fresh on every turn. */
const PlanChat: React.FC<PlanChatProps> = ({ chat, planVersion }) => {
  const { messages, streaming, error, chatVersion, send, retry } = chat;
  const [draft, setDraft] = useState('');
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const stale = chatVersion !== null && planVersion > chatVersion && !streaming;
  const lastContent = messages[messages.length - 1]?.content ?? '';

  useEffect(() => {
    input.current?.focus();
  }, []);

  // Growth scrolls itself into view: follow the reply as it streams.
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, lastContent, error, stale]);

  const submit = (text: string) => {
    if (!text.trim() || streaming) return;
    send(text, planVersion);
    setDraft('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit(draft);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite" aria-busy={streaming}>
        {messages.length === 0 && !error ? (
          <div className="px-1 pb-4 pt-2">
            <p className="text-sm text-on-surface">Ask anything about this plan: its topics, notes, votes and decisions.</p>
            <p className="mt-1 text-xs text-on-surface-variant">The conversation stays in this tab. Nothing is saved.</p>
            <div className="mt-4 flex flex-col items-start gap-1">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => submit(s)}
                  className={`np-state rounded-full bg-surface-container-high px-3 py-1.5 text-left text-xs text-on-surface ${MOTION} ${FOCUS}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ol className="mb-0 space-y-3 pb-2">
            {messages.map((m, i) => {
              const live = streaming && i === messages.length - 1 && m.role === 'assistant';
              return (
                <li key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex'}>
                  <div
                    className={
                      m.role === 'user'
                        ? 'max-w-[85%] rounded-2xl rounded-br-md bg-primary/15 px-3 py-2 text-sm text-on-surface'
                        : 'max-w-full px-1 text-sm leading-relaxed text-on-surface'
                    }
                  >
                    <span className="sr-only">{m.role === 'user' ? 'You: ' : 'Answer: '}</span>
                    {m.content ? <Paragraphs text={m.content} /> : null}
                    {live && (
                      <span
                        aria-label="Writing"
                        className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 rounded-sm bg-on-surface-variant motion-safe:animate-pulse"
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <Reveal show={!!error}>
          {error && (
            <Alert variant="note" onRetry={error.retryable ? retry : undefined} retryLabel="Retry" retryTip="Send the question again" className="my-1">
              {error.message}
            </Alert>
          )}
        </Reveal>
        <div ref={end} />
      </div>

      <Reveal show={!!stale}>
      {stale && (
        <div className="flex justify-center py-2">
          <button
            type="button"
            onClick={() => send(LATEST_PROMPT, planVersion)}
            className={buttonClass('tonal', 'sm')}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 text-primary" fill="none">
              <path fill="currentColor" d="M12 4a8 8 0 0 1 7.4 5H17v2h6V5h-2v2.3A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8zm8 8a8 8 0 0 1-15.4 3H7v-2H1v6h2v-2.3A10 10 0 0 0 22 12h-2z" />
            </svg>
            Plan changed. Send latest
          </button>
        </div>
      )}
      </Reveal>

      <div className="pt-2">
        <label htmlFor="np-chat-input" className="sr-only">
          Ask about this plan
        </label>
        <TextArea
          id="np-chat-input"
          ref={input}
          rows={2}
          value={draft}
          maxLength={4000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={streaming ? 'Answering...' : 'Ask a question. Enter sends, Shift+Enter adds a line.'}
          className="max-h-40 min-h-[44px] !resize-none"
        />
      </div>
    </div>
  );
};

export default PlanChat;
